import styled from '@emotion/styled';
import { type EntityEvent, EntityEventLabels } from '@racemap/sdk/schema/entityEvents';
import { formatLabel } from '@racemap/utilities/formatting';
import { Button, Space, Typography, notification } from 'antd';
import { toast } from 'react-toastify';
import { IconClose } from 'src/components/Icon';
import { entityEventColorMap } from 'src/components/MonitorBeta/utils/entityEventColorMap';
import { getRelativeTime } from 'src/components/utils/timeUtils';

const { Title, Text } = Typography;

export interface AlertNotificationProps {
  alert: EntityEvent;
  starterNumber: string;
  starterName: string;
  onAssign: (alertId: string) => Promise<void>;
  onResolve: (alertId: string) => Promise<void>;
}

export function showAlertNotification({
  alert,
  starterNumber,
  starterName,
  onAssign,
  onResolve,
}: AlertNotificationProps) {
  const eventType = alert.eventType;
  const idHex = alert.id.toHexString();

  notification.open({
    message: (
      <Title level={4} style={{ color: '#fff' }}>
        {EntityEventLabels[eventType]}
      </Title>
    ),
    description: (
      <Text style={{ color: '#fff' }}>
        Starter <u>{formatLabel([starterNumber, starterName])}</u> received an alert{' '}
        <u>{getRelativeTime(alert.occurredAt)}</u> ago.
      </Text>
    ),
    btn: (
      <Space>
        <NotificationButton size="small" aria-label="Assign to myself">
          <Text
            style={{ color: entityEventColorMap[eventType] }}
            onClick={async () => {
              try {
                await onAssign(idHex);
                notification.destroy();
                toast('Alert assigned to yourself!');
              } catch (err) {
                console.error(err);
                toast('Failed to assign alert.');
              }
            }}
          >
            Assign to myself
          </Text>
        </NotificationButton>
        <NotificationButton size="small" aria-label="Resolve alert">
          <Text
            style={{ color: entityEventColorMap[eventType] }}
            onClick={async () => {
              try {
                await onResolve(idHex);
                notification.destroy();
                toast('Alert resolved successfully!');
              } catch (err) {
                console.error(err);
                toast('Failed to resolve alert.');
              }
            }}
          >
            Resolve
          </Text>
        </NotificationButton>
      </Space>
    ),
    style: {
      backgroundColor: entityEventColorMap[eventType],
      borderRadius: '8px',
    },
    closeIcon: <IconClose style={{ color: '#fff' }} />,
  });
}

const NotificationButton = styled(Button)`
  background-color: #fff;
  transition: background-color 0.2s ease-in-out, transform 0.2s ease-in-out;

  &.ant-btn-variant-outlined:not(:disabled):not(.ant-btn-disabled):hover,
  &.ant-btn-variant-dashed:not(:disabled):not(.ant-btn-disabled):hover {
    border-color: #ddd;
  }
  &:hover {
    transform: scale(1.03);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;
